/**
 * Step-up super-admin passphrase plumbing.
 *
 * UX:
 *   - Read-only super-admin pages (Tenants Health, Markets list, AppVersion view)
 *     fire ZERO prompts.
 *   - The first DESTRUCTIVE call in a tab (delete tenant, market grant /
 *     release / zips edit / price change, app-version push) prompts for the
 *     passphrase, caches it in sessionStorage for the rest of the tab.
 *   - On 403 with the destructive_passphrase_required reason, the cached
 *     value is wiped so the next attempt re-prompts.
 *
 * Storage:
 *   sessionStorage (cleared when the tab closes — never localStorage, so
 *   a stolen disk image never carries the passphrase).
 *
 * Header sent: X-Super-Admin-Destructive-Passphrase
 */
import axios from 'axios';

const STORAGE_KEY = 'super_admin_destructive_passphrase';
const HEADER = 'X-Super-Admin-Destructive-Passphrase';

// Routes that perform destructive super-admin operations. The interceptor
// only prompts when one of these is being called. Read-only super-admin
// endpoints (lists, previews, dashboards) are unaffected.
//
// Patterns are matched against the request URL. Keep this list in sync
// with the backend controllers that call require_destructive_passphrase.
const DESTRUCTIVE_PATTERNS = [
  /\/super_admin\/markets\/grant(\?|$)/,
  /\/super_admin\/markets\/[^/]+\/release(\?|$)/,
  /\/super_admin\/markets\/[^/]+\/zips(\?|$)/,
  /\/super_admin\/markets\/[^/]+\/price(\?|$)/,
  /\/super_admin\/markets\/[^/]+\/billing_date(\?|$)/,
  /\/super_admin\/markets\/[^/]+\/coupon(\?|$)/,
  /\/super_admin\/tenants\/[^/]+\/delete(\?|$)/,
  /\/super_admin\/deployment\/teams\/[^/]+\/delete(\?|$)/,
  /\/app-version\/(ios|android)(\?|$)/,
];

function isDestructive(method, url) {
  const m = String(method || '').toUpperCase();
  if (m !== 'POST' && m !== 'PUT' && m !== 'DELETE' && m !== 'PATCH') return false;
  const u = String(url || '');
  return DESTRUCTIVE_PATTERNS.some((re) => re.test(u));
}

export function getPassphrase() {
  try {
    return window.sessionStorage.getItem(STORAGE_KEY);
  } catch (_) {
    return null;
  }
}

export function setPassphrase(value) {
  try {
    if (value) window.sessionStorage.setItem(STORAGE_KEY, value);
    else window.sessionStorage.removeItem(STORAGE_KEY);
  } catch (_) {
    // sessionStorage unavailable (private mode etc.) — fall through.
  }
}

export function clearPassphrase() {
  setPassphrase(null);
}

export function promptForPassphrase() {
  // eslint-disable-next-line no-alert
  const value = window.prompt(
    'Super-admin passphrase (required for destructive actions):',
  );
  if (value) setPassphrase(value);
  return value || null;
}

export function ensurePassphrase() {
  let p = getPassphrase();
  if (!p) p = promptForPassphrase();
  return p;
}

let installed = false;

/**
 * Idempotent. Installs a global axios request/response interceptor that
 * stamps the passphrase header on destructive super-admin calls and
 * clears the cached value on 403.
 */
export function installSuperAdminAxiosInterceptor() {
  if (installed) return;
  installed = true;

  axios.interceptors.request.use((cfg) => {
    if (isDestructive(cfg.method, cfg.url || '')) {
      const p = ensurePassphrase();
      if (p) {
        cfg.headers = { ...(cfg.headers || {}), [HEADER]: p };
      }
    }
    return cfg;
  });

  axios.interceptors.response.use(
    (r) => r,
    (error) => {
      const status = error && error.response && error.response.status;
      const cfg = error && error.config;
      if (status === 403 && cfg && isDestructive(cfg.method, cfg.url || '')) {
        // Wrong passphrase, missing passphrase, allowlist miss, or
        // origin block — wipe so the next attempt re-prompts. The
        // operator can also try a different browser if origin is the
        // problem.
        clearPassphrase();
      }
      return Promise.reject(error);
    },
  );
}

export const SUPER_ADMIN_PASSPHRASE_HEADER = HEADER;
